import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  X, Trash2, Package, Search, Send, Loader2, Plus, AlertTriangle, ArrowRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { WASTE_CATEGORIES } from '../../../../services/wasteTrackingService';

/**
 * LogWasteModal
 * ──────────────────────────────────────────────────────────────────────────
 * Popup form for logging one or more waste entries in a single session —
 * same modal anatomy as PurchaseOrderForm (fixed overlay, rounded-2xl card,
 * header / body / footer) and the same line-items-table pattern for adding
 * multiple rows.
 *
 * Each row is its own item + qty + category + reason, since waste entries
 * (unlike PO items) don't share a single category/reason. Submitting posts
 * one waste entry per row — the underlying API has no bulk endpoint — and
 * reports a single combined result instead of one toast per row.
 *
 * Mandatory fields per row: Item, Qty (> 0 and <= current stock), Category
 * (waste type), Reason, and the resulting Stock Impact must not go negative.
 *
 * Props:
 *  - inventoryItems: array of items to search/select from
 *  - prefill: optional { itemId, itemName, category } — used when opened via
 *             a Quick Log card, so the first row starts pre-selected
 *  - onClose(): called on cancel / backdrop / Esc
 *  - onSubmit(payload): async — parent posts ONE waste entry via the
 *             existing logWaste() hook function. Called once per row here.
 */

const emptyRow = (overrides = {}) => ({
  _key: `${Date.now()}-${Math.random()}`,
  itemId: null,
  itemName: '',
  currentStock: 0,
  uomSymbol: '',
  quantity: '',
  category: '',
  reason: '',
  ...overrides,
});

export default function LogWasteModal({ inventoryItems = [], prefill = null, onClose, onSubmit }) {
  const [rows, setRows] = useState([
    emptyRow(
      prefill
        ? { itemId: prefill.itemId ?? null, itemName: prefill.itemName ?? '', category: prefill.category ?? '' }
        : {},
    ),
  ]);
  const [errors, setErrors] = useState({}); // keyed by `${rowKey}_field`
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Per-row item dropdown (fixed-position, same approach as PurchaseOrderForm) ──
  const [openItemDropdown, setOpenItemDropdown] = useState(null); // row _key or null
  const [itemSearch, setItemSearch] = useState('');
  const [dropdownPos, setDropdownPos] = useState(null);
  const itemDropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (itemDropdownRef.current && !itemDropdownRef.current.contains(e.target)) {
        setOpenItemDropdown(null);
        setDropdownPos(null);
        setItemSearch('');
      }
    };
    const handleKey = (e) => { if (e.key === 'Escape' && !openItemDropdown) onClose?.(); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose, openItemDropdown]);

  // ── Lock background scroll while the modal is open ──────────────────────
  // Without this, the page behind the overlay keeps its own scrollbars
  // (e.g. the dashboard's horizontal scroll), which shows through/behind
  // the modal and can look like the modal itself is scrolling.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const filteredInventoryItems = useMemo(() => {
    if (!itemSearch) return inventoryItems;
    const q = itemSearch.toLowerCase();
    return (inventoryItems || []).filter((it) => it?.name?.toLowerCase()?.includes(q));
  }, [inventoryItems, itemSearch]);

  // ── Row helpers ──────────────────────────────────────────────────────────
  const updateRow = (idx, field, value) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const selectItem = (idx, item) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        itemId: item.id,
        itemName: item.name,
        currentStock: item.currentStock || 0,
        uomSymbol: item.uomSymbol || item.uom || '',
      };
      return updated;
    });
    setOpenItemDropdown(null);
    setDropdownPos(null);
    setItemSearch('');
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  // ── Stock impact helper — remaining stock after this row's waste qty ───────
  const stockImpact = (row) => {
    const qty = parseFloat(row.quantity);
    if (!row.itemId || Number.isNaN(qty)) return null;
    return row.currentStock - qty;
  };

  // ── Validation ───────────────────────────────────────────────────────────
  // Mandatory: item, quantity (>0), category (waste type), reason, and the
  // resulting stock impact must not be negative.
  const validate = () => {
    const errs = {};
    rows.forEach((r) => {
      if (!r.itemId) errs[`${r._key}_item`] = 'Select an item';
      const qty = parseFloat(r.quantity);
      if (!r.quantity || Number.isNaN(qty) || qty <= 0) {
        errs[`${r._key}_qty`] = 'Qty > 0';
      } else if (r.itemId && (r.currentStock - qty) < 0) {
        errs[`${r._key}_qty`] = 'Exceeds stock';
      }
      if (!r.category) errs[`${r._key}_category`] = 'Required';
      if (!r.reason || !r.reason.trim()) errs[`${r._key}_reason`] = 'Reason required';
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Submit — one waste entry per row, sequentially ──────────────────────
  const handleSubmit = async () => {
    if (rows.length === 0) {
      toast.error('Add at least one item');
      return;
    }
    if (!validate()) {
      toast.error('Please fix the highlighted rows');
      return;
    }

    setIsSubmitting(true);
    const failedRows = [];
    let successCount = 0;

    for (const row of rows) {
      try {
        await onSubmit({
          inventoryItemId: Number(row.itemId),
          quantity: parseFloat(row.quantity) || 0,
          wasteCategory: row.category.toLowerCase(),
          reason: row.reason.trim(),
        });
        successCount += 1;
      } catch (err) {
        const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed';
        failedRows.push({ ...row, _error: message });
      }
    }

    setIsSubmitting(false);

    if (failedRows.length === 0) {
      toast.success(successCount === 1 ? 'Waste entry logged' : `${successCount} waste entries logged`);
      onClose();
    } else if (successCount === 0) {
      toast.error('Failed to log waste entries — see details below');
      setRows(failedRows);
    } else {
      toast.error(`${successCount} logged, ${failedRows.length} failed — fix and retry`);
      setRows(failedRows);
    }
  };

  return (
    <div
      className="waste-modal-root fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onClose}
    >
      {/* Hide native number-input spinner arrows, matching PurchaseOrderForm */}
      <style>{`
        .waste-modal-root input[type=number]::-webkit-outer-spin-button,
        .waste-modal-root input[type=number]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .waste-modal-root input[type=number] {
          -moz-appearance: textfield;
          appearance: textfield;
        }
      `}</style>

      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[92vh] flex flex-col overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-teal-600" />
            <h2 className="text-base font-bold text-slate-800">Log Waste Entry</h2>
            {rows.length > 1 && (
              <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600">
                {rows.length} items
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Waste Items</h3>
            <button onClick={addRow} className="flex items-center gap-1 px-2 py-1 text-xs text-teal-600 hover:bg-teal-50 rounded-lg transition-colors font-medium">
              <Plus className="w-3.5 h-3.5" /> Add Item
            </button>
          </div>

          <div className="border border-slate-200 rounded-lg">
            <table className="w-full text-xs table-fixed">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left   px-3 py-2 font-semibold text-slate-600 w-[20%] rounded-tl-lg">
                    Item <span className="text-red-500">*</span>
                  </th>
                  <th className="text-center px-3 py-2 font-semibold text-slate-600 w-[9%]">
                    Qty <span className="text-red-500">*</span>
                  </th>
                  <th className="text-left   px-3 py-2 font-semibold text-slate-600 w-[19%]">Stock Impact</th>
                  <th className="text-left   px-3 py-2 font-semibold text-slate-600 w-[16%]">
                    Waste Type <span className="text-red-500">*</span>
                  </th>
                  <th className="text-left   px-3 py-2 font-semibold text-slate-600 w-[31%]">
                    Reason <span className="text-red-500">*</span>
                  </th>
                  <th className="w-[5%] rounded-tr-lg"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row, idx) => {
                  const impact = stockImpact(row);
                  return (
                  <React.Fragment key={row._key}>
                    <tr className="hover:bg-slate-50/40">
                      {/* Item selector */}
                      <td className="px-3 py-2" ref={openItemDropdown === row._key ? itemDropdownRef : null}>
                        <button
                          type="button"
                          onClick={(e) => {
                            if (openItemDropdown === row._key) {
                              setOpenItemDropdown(null); setDropdownPos(null);
                            } else {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 220) });
                              setOpenItemDropdown(row._key);
                              setItemSearch('');
                            }
                          }}
                          className={`w-full text-left px-2 py-1.5 border rounded text-xs ${
                            errors[`${row._key}_item`] ? 'border-red-300 bg-red-50' : 'border-slate-200'
                          }`}
                        >
                          {row.itemName || <span className="text-slate-400">Select item...</span>}
                        </button>
                        {errors[`${row._key}_item`] && <p className="text-[10px] text-red-500 mt-0.5">{errors[`${row._key}_item`]}</p>}

                        {openItemDropdown === row._key && dropdownPos && (
                          <div
                            className="fixed bg-white border border-slate-200 rounded-lg shadow-xl z-[100] py-1"
                            style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
                          >
                            <div className="px-2 pb-1">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                                <input
                                  autoFocus
                                  type="text"
                                  placeholder="Search items..."
                                  value={itemSearch}
                                  onChange={(e) => setItemSearch(e.target.value)}
                                  className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-teal-400"
                                />
                              </div>
                            </div>
                            <div className="max-h-48 overflow-y-auto">
                              {filteredInventoryItems.length === 0 ? (
                                <p className="px-3 py-2 text-xs text-slate-400">No items found</p>
                              ) : (
                                filteredInventoryItems.map((item) => (
                                  <button
                                    key={item.id}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => selectItem(idx, item)}
                                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 text-slate-700 flex items-center justify-between"
                                  >
                                    <span>{item.name}</span>
                                    <span className="text-slate-400">{(item.currentStock || 0).toFixed(1)} in stock</span>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Quantity */}
                      <td className="px-3 py-2">
                        <input
                          type="number" min="0" step="0.01"
                          value={row.quantity}
                          onChange={(e) => updateRow(idx, 'quantity', e.target.value)}
                          onWheel={(e) => e.currentTarget.blur()}
                          placeholder="0.00"
                          className={`w-full text-center px-2 py-1.5 border rounded text-xs ${
                            errors[`${row._key}_qty`] ? 'border-red-300 bg-red-50' : 'border-slate-200'
                          } focus:outline-none focus:ring-1 focus:ring-teal-400`}
                        />
                        {errors[`${row._key}_qty`] && <p className="text-[10px] text-red-500 mt-0.5 text-center">{errors[`${row._key}_qty`]}</p>}
                      </td>

                      {/* Stock Impact — computed, read-only, always visible so the
                          person can see the effect on stock before submitting.
                          Shown as "current → new unit", left-aligned like the
                          rest of the row rather than stacked/centered. */}
                      <td className="px-3 py-2">
                        {row.itemId && impact !== null ? (
                          <div className={`flex items-center gap-1 whitespace-nowrap px-2 py-1.5 rounded ${
                            impact < 0 ? 'bg-red-50' : impact === 0 ? 'bg-amber-50' : 'bg-slate-50'
                          }`}>
                            <span className="text-slate-500">{row.currentStock.toFixed(2)}</span>
                            <ArrowRight size={11} className={impact < 0 ? 'text-red-400' : 'text-amber-500'} />
                            <span className={`font-semibold ${impact < 0 ? 'text-red-600' : impact === 0 ? 'text-amber-600' : 'text-slate-900'}`}>
                              {impact.toFixed(2)}
                            </span>
                            {row.uomSymbol && <span className="text-slate-400">{row.uomSymbol}</span>}
                          </div>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>

                      {/* Category / Waste Type */}
                      <td className="px-3 py-2">
                        <select
                          value={row.category}
                          onChange={(e) => updateRow(idx, 'category', e.target.value)}
                          className={`w-full px-2 py-1.5 border rounded text-xs bg-white ${
                            errors[`${row._key}_category`] ? 'border-red-300 bg-red-50' : 'border-slate-200'
                          } focus:outline-none focus:ring-1 focus:ring-teal-400`}
                        >
                          <option value="">Select...</option>
                          {WASTE_CATEGORIES.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                        {errors[`${row._key}_category`] && <p className="text-[10px] text-red-500 mt-0.5">{errors[`${row._key}_category`]}</p>}
                      </td>

                      {/* Reason — now mandatory for every row */}
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={row.reason}
                          onChange={(e) => updateRow(idx, 'reason', e.target.value)}
                          placeholder="Required — describe..."
                          className={`w-full px-2 py-1.5 border rounded text-xs ${
                            errors[`${row._key}_reason`] ? 'border-red-300 bg-red-50' : 'border-slate-200'
                          } focus:outline-none focus:ring-1 focus:ring-teal-400`}
                        />
                        {errors[`${row._key}_reason`] && <p className="text-[10px] text-red-500 mt-0.5">{errors[`${row._key}_reason`]}</p>}
                      </td>

                      {/* Remove */}
                      <td className="px-3 py-2 text-center">
                        {rows.length > 1 && (
                          <button onClick={() => removeRow(idx)} className="p-1 text-slate-400 hover:text-red-500 rounded transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                    {row._error && (
                      <tr>
                        <td colSpan={6} className="px-3 pb-2">
                          <div className="flex items-center gap-1.5 text-[11px] text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1">
                            <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                            {row._error}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Stock warnings — flag any row that would take stock to/below 0 */}
          {rows.some((r) => r.itemId && parseFloat(r.quantity) > 0 && (r.currentStock - parseFloat(r.quantity)) <= 0) && (
            <div className="mt-3 flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-amber-700">
                One or more items will be reduced to 0 stock or below — double-check the quantities before submitting.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {isSubmitting ? 'Logging...' : rows.length > 1 ? `Log ${rows.length} Items` : 'Log Waste'}
          </button>
        </div>
      </div>
    </div>
  );
}